import { createFileRoute } from "@tanstack/react-router";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";
import { ToolCard } from "@/components/calv/ToolCard";
import { Link } from "@tanstack/react-router";
import { jsonLdScript, breadcrumbSchema, pageHead } from "@/lib/seo";
import { SITE_URL } from "@/lib/locale";

export const Route = createFileRoute("/leistungen/tools")({
  head: () => {
    const h = pageHead("tools", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
            jsonLdScript(breadcrumbSchema([
              { name: "Start", path: "/" },
              { name: "Leistungen", path: "/leistungen" },
              { name: "Tools", path: "/leistungen/tools" },
            ])),
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              name: "Tools — Calvarius",
              url: `${SITE_URL}/leistungen/tools`,
              hasPart: [
                { "@type": "SoftwareApplication", name: "Delta", applicationCategory: "BusinessApplication", url: `${SITE_URL}/leistungen/delta` },
                { "@type": "SoftwareApplication", name: "picazzoo", applicationCategory: "BusinessApplication", url: `${SITE_URL}/leistungen/picazzoo` },
                { "@type": "SoftwareApplication", name: "GonzalAds", applicationCategory: "BusinessApplication", url: `${SITE_URL}/leistungen/gonzalads` },
              ],
            }),
          ],
    };
  },
  component: ToolsHubDE,
});

function ToolsHubDE() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Tools"
          headline={<>Wir bauen die Werkzeuge, mit denen <em>wir arbeiten.</em></>}
          lead="Performance-Mandate skalieren nicht durch mehr Personal, sondern durch bessere Werkzeuge. Calvarius hat in den letzten Jahren drei eigene Tools entwickelt, die genau dort Geschwindigkeit, Genauigkeit und Wirtschaftlichkeit erzeugen, wo Standard-Software an ihre Grenzen stößt — Feed-Management, Creative-Produktion und Meta-Kampagnen-Steuerung. Plus die Fähigkeit, individuelle Software für Mandate in einem Bruchteil der bisherigen Zeit zu bauen."
          metaStrip={["Drei eigene Tools", "KI-gestützte Entwicklung", "Für Mandate optimiert"]}
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Tools" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Kein Standard-Tooling. <em>Sondern eigene Software.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>Die meisten Agenturen nutzen Standard-Software für alle Mandate. Das ist effizient für die Agentur, aber selten optimal für den Kunden — Lizenzkosten skalieren mit dem Volumen, Workflows passen sich nur eingeschränkt an Mandate-Spezifika an, und bei jeder Plattform-Änderung wartet man auf Updates aus Drittanbieter-Roadmaps.</p>
              <p>Wir gehen einen anderen Weg. Dort, wo Standard-Tools systematisch zu langsam, zu teuer oder zu starr werden, bauen wir eigene Software. Drei Werkzeuge sind heute produktiv im Einsatz: <strong className="font-semibold text-ink">Delta</strong> für Feed-Management, <strong className="font-semibold text-ink">picazzoo</strong> für KI-gestützte Creative-Produktion, <strong className="font-semibold text-ink">GonzalAds</strong> für Meta-Kampagnen-Steuerung.</p>
              <p>Diese Tools sind kein Selbstzweck. Sie entstehen aus konkreten Engpässen in Mandaten — und werden weiterentwickelt, sobald sich neue Engpässe zeigen. Das gleiche Prinzip wenden wir auf individuelle Software-Projekte für unsere Mandate an: KI-gestützte Entwicklungswerkzeuge ermöglichen heute, dass Custom-Lösungen in Wochen entstehen, wo früher Monate nötig waren.</p>
            </div>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="03" kicker="Drei Tools" headline={<>Produktiv im Einsatz. <em>Nicht im Pitch-Deck.</em></>}>
          <div className="grid gap-4 md:grid-cols-3">
            <ToolCard
              brand="Delta"
              tagline="Feed-Management. Skaliert. Versioniert. Ohne Lizenzkosten."
              bullets={["Saubere Pipelines pro Plattform", "Plattformspezifische Optimierung statt One-Size-Feeds", "Versioniert, auditierbar"]}
              href="/leistungen/delta"
              cta="Mehr erfahren"
            />
            <ToolCard
              brand="picazzoo"
              tagline="KI-Creatives. Banner, Videos, Produkt-Galerien — automatisiert."
              bullets={["Werbebanner und -videos für alle Performance-Plattformen", "Produkt-Gallery-Bilder unter Einbindung von Produktdaten", "Brand-konsistent über Kanäle hinweg"]}
              href="/leistungen/picazzoo"
              cta="Mehr erfahren"
            />
            <ToolCard
              brand="GonzalAds"
              tagline="Meta-Kampagnen-Steuerung. Schneller, präziser, tiefer als nativ."
              bullets={["KI-Wizard für Kampagnen, Anzeigengruppen, Ads", "Regelbasierte Automatisierung von Optimierung und Skalierung", "Performance-Analysen weit über die nativen Meta-Reports hinaus"]}
              href="/leistungen/gonzalads"
              cta="Mehr erfahren"
            />
          </div>
        </Section>

        <Section number="04" kicker="Custom-SaaS-Entwicklung" headline={<>Wenn keines der drei Tools passt, <em>bauen wir eines.</em></>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl space-y-5 text-[17px] leading-relaxed text-ink-2">
              <p>Manche Probleme lassen sich nicht mit Standard-Software lösen — sie brauchen individuelle Werkzeuge. Datenpipelines, interne Dashboards, Konfiguratoren, kleine SaaS-Bausteine, Schnittstellen-Integrationen.</p>
              <p>Was sich verändert hat: Mit KI-gestützten Entwicklungswerkzeugen lassen sich individuelle Software-Projekte heute in einem Bruchteil der Zeit realisieren, die früher nötig war. Damit wird Custom-Software für eine Größenordnung mehr Mandate wirtschaftlich, die früher auf Standard-Tools ausweichen mussten.</p>
              <p>Wir entwickeln nicht jeden Wunsch, sondern dort, wo individuelle Software echten wirtschaftlichen Hebel hat. Wenn Sie ein Werkzeug brauchen, das es so nicht gibt, sprechen wir gerne darüber.</p>
            </div>
          </RevealOnScroll>
          <div className="mt-10">
            <CTABlock
              headline={<>Idee <em>besprechen?</em></>}
              body="Skizzieren Sie kurz, was fehlt — wir prüfen, ob individuelle Software hier wirklich der richtige Hebel ist."
              primary={{ label: "Idee besprechen", to: "/kontakt" }}
              secondary={{ label: "Entwicklung digitaler Dienste", to: "/leistungen/entwicklung-digitaler-dienste" }}
            />
          </div>
        </Section>

        <Section kicker="Hinweis" headline={<>Öffentliche <em>Analyse-Werkzeuge</em></>}>
          <div className="max-w-2xl">
            <p className="text-[17px] leading-relaxed text-ink-2">
              Calvarius betreibt zusätzlich öffentliche Analyse-Werkzeuge zur Eigen-Diagnose — Lighthouse-Check und Wirtschaftlichkeits-Rechner. Diese sind unter{" "}
              <Link to="/tools" className="text-cyan-deep underline decoration-cyan/40 underline-offset-4 hover:decoration-cyan-deep">
                Calvarius-Werkzeuge
              </Link>{" "}zugänglich.
            </p>
          </div>
        </Section>

        <LogoReferences locale="de" number="05" />

        <RelatedServices
          locale="de"
          fromId="tools"
          number="06"
          items={relatedBySource["tools"]}
        />

        <Section variant="deep" number="07" kicker="Demo">
          <CTABlock
            variant="deep"
            headline={<>Sehen, wie unsere Tools im <em>Mandat arbeiten?</em></>}
            body="Eine 30-minütige Demo zeigt mehr als jede Folie. Wir zeigen Ihnen DELTA, picazzoo oder GonzalAds an einem realen Beispiel — entweder live oder anhand eines vergleichbaren Mandate-Setups."
            primary={{ label: "Demo anfordern", to: "/kontakt" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
